import React, { useEffect, useState } from 'react'
import { designTokens } from '@/utils/design-tokens'
import { SnapshotService } from '@/api/generated/services/SnapshotService'
import type { SnapshotItemResponse } from '@/api/generated/models/SnapshotItemResponse'

interface SnapshotSelectProps {
  /** weekly | monthly */
  period: 'weekly' | 'monthly'
  /** 선택된 스냅샷 as_of (없으면 미선택) */
  value: string | null
  /** 선택 변경 시 호출 */
  onChange: (snapshot: SnapshotItemResponse) => void
  className?: string
}

const selectStyle: React.CSSProperties = {
  backgroundColor: designTokens.colors.white,
  borderColor: designTokens.colors.gray[2],
  fontFamily: designTokens.typography.fontFamily.sans[0],
  fontSize: designTokens.typography.fontSize['body-medium'][0],
  color: designTokens.colors.gray[6],
  minWidth: '240px',
}

/** 조회 가능한 스냅샷(주간/월간) 셀렉터 — GET /api/snapshots */
const SnapshotSelect: React.FC<SnapshotSelectProps> = ({ period, value, onChange, className = '' }) => {
  const [snapshots, setSnapshots] = useState<SnapshotItemResponse[]>([])
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setIsLoading(true)
      try {
        const res = await SnapshotService.appsSnapshotViewsListSnapshots({ period })
        if (cancelled) return
        const list = res ?? []
        setSnapshots(list)
        // 현재 선택값이 목록에 없으면 최신(첫 항목) 자동 선택
        if (list.length > 0 && !list.some(s => s.as_of === value)) {
          onChange(list[0])
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load snapshots:', err)
          setSnapshots([])
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
    // value/onChange 변경으로 재조회하지 않음 (period 변경 시에만)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period])

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const snap = snapshots.find(s => s.as_of === e.target.value)
    if (snap) onChange(snap)
  }

  // 'YYYY-MM-DD' → 'M월 D일'
  const formatKoreanDate = (d: string) => {
    const [, m, day] = d.split('-')
    return `${Number(m)}월 ${Number(day)}일`
  }

  // 라벨 + 저장 시점(기간 종료일) — 예: "2026년 6월 (6월 30일 시점)", "2026년 25주차 (6월 21일 시점)"
  const optionLabel = (s: SnapshotItemResponse) =>
    s.period_end
      ? `${s.label} (${formatKoreanDate(s.period_end)} 시점)`
      : s.label

  const isEmpty = !isLoading && snapshots.length === 0

  return (
    <div className={`relative flex-shrink-0 ${className}`}>
      <select
        value={value ?? ''}
        onChange={handleChange}
        disabled={isLoading || isEmpty}
        className="h-10 px-3 border-2 rounded cursor-pointer outline-none appearance-none pr-8"
        style={selectStyle}
      >
        {isLoading ? (
          <option value="">불러오는 중...</option>
        ) : isEmpty ? (
          <option value="">조회 가능한 기간 없음</option>
        ) : (
          snapshots.map(s => (
            <option key={s.as_of} value={s.as_of}>
              {optionLabel(s)}
            </option>
          ))
        )}
      </select>
      <div
        className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none"
        style={{ color: designTokens.colors.gray[4] }}
      >
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    </div>
  )
}

export default SnapshotSelect
